/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Switch,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonTypography from "components/ArgonTypography";
import Dropdown from "components/CustomDropdown/Dropdown";
import PaginationBar from "components/PaginationBar/PaginationBar";
import { toast } from "react-toastify";
import {
  getServiceReviewIncidents,
  getServiceReviewRules,
  getServiceReviews,
  updateServiceReview,
  updateServiceReviewRule,
} from "services/serviceReviewService";
import { AdminUnreadBadge } from "components/AdminUnread";
import {
  ADMIN_UNREAD_MODULES,
  markAdminUnreadSeen,
} from "services/adminUnreadService";

const labels = {
  LATE_ARRIVAL: "Late Arrival",
  EARLY_CHECKOUT: "Early Check-out",
  IRREGULAR_CLASS_GAP: "Irregular Class Gap",
};
const statuses = ["ADMIN_ALERTED", "AWAITING_PARENT", "OPEN", "IN_PROGRESS", "RESOLVED", "DISMISSED"];
const statusColors = {
  ADMIN_ALERTED: "warning",
  AWAITING_PARENT: "info",
  OPEN: "error",
  IN_PROGRESS: "warning",
  RESOLVED: "success",
  DISMISSED: "secondary",
};
const nameOf = (value) => value?.fullName || value?.name || "—";
const pretty = (value) => String(value || "—").replaceAll("_", " ");

const ValueCell = ({ children }) => (
  <ArgonTypography variant="caption" color="text" fontWeight="medium">
    {children}
  </ArgonTypography>
);

export default function ServiceReviewPage() {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [rules, setRules] = useState([]);
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [summary, setSummary] = useState({ active: 0, pending: 0, resolved: 0 });
  const [selected, setSelected] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [form, setForm] = useState({ status: "IN_PROGRESS", resolutionNote: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reviewResponse, ruleResponse] = await Promise.all([
        getServiceReviews({ status, incidentType: type, page, limit }),
        getServiceReviewRules(),
      ]);
      setReviews(reviewResponse.data.data || []);
      setPagination(reviewResponse.data.pagination || { total: 0, totalPages: 0 });
      setSummary(reviewResponse.data.summary || { active: 0, pending: 0, resolved: 0 });
      setRules(ruleResponse.data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Service reviews not loaded");
    } finally {
      setLoading(false);
    }
  }, [limit, page, status, type]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [status, type]);

  const counts = useMemo(() => ({
    active: Number(summary.active || 0),
    pending: Number(summary.pending || 0),
    resolved: Number(summary.resolved || 0),
  }), [summary]);

  const openReview = async (review) => {
    setSelected(review);
    if (review.adminUnread) {
      try {
        await markAdminUnreadSeen(ADMIN_UNREAD_MODULES.SERVICE_REVIEW, review._id);
        setReviews((oldReviews) =>
          oldReviews.map((item) =>
            item._id === review._id
              ? { ...item, adminUnread: false, adminSeenAt: new Date().toISOString() }
              : item
          )
        );
        setSelected({ ...review, adminUnread: false, adminSeenAt: new Date().toISOString() });
      } catch (error) {
        console.error("Error marking service review as seen:", error);
      }
    }
    setForm({
      status: ["RESOLVED", "DISMISSED"].includes(review.status) ? review.status : "IN_PROGRESS",
      resolutionNote: review.resolutionNote || "",
    });
    try {
      setIncidents((await getServiceReviewIncidents(review._id)).data.data || []);
    } catch {
      setIncidents([]);
    }
  };

  const tableData = useMemo(() => ({
    columns: [
      { name: "sno", label: "S.No.", align: "center" },
      { name: "issue", label: "Issue", align: "left" },
      { name: "tutor", label: "Tutor", align: "left" },
      { name: "student", label: "Student", align: "left" },
      { name: "cycle", label: "Cycle", align: "center" },
      { name: "count", label: "Count", align: "center" },
      { name: "parent", label: "Parent Response", align: "left" },
      { name: "status", label: "Status", align: "left" },
      { name: "created", label: "Created At", align: "left" },
      { name: "action", label: "Action", align: "center" },
    ],
    rows: reviews.map((review, index) => ({
      hasBorder: true,
      sno: <ValueCell>{(page - 1) * limit + index + 1}</ValueCell>,
      issue: (
        <ArgonBox display="flex" alignItems="center" gap={0.75}>
          <ArgonTypography
            variant="caption"
            color={review.adminUnread ? "error" : "text"}
            fontWeight={review.adminUnread ? "bold" : "medium"}
          >
            {labels[review.incidentType]}
          </ArgonTypography>
          <AdminUnreadBadge unread={Boolean(review.adminUnread)} />
        </ArgonBox>
      ),
      tutor: <ValueCell>{nameOf(review.tutorId)}</ValueCell>,
      student: <ValueCell>{nameOf(review.studentId)}</ValueCell>,
      cycle: <ValueCell>{review.cycleIndex + 1}</ValueCell>,
      count: <ValueCell>{review.occurrenceCount}</ValueCell>,
      parent: <ValueCell>{pretty(review.parentResponse)}</ValueCell>,
      status: <Chip size="small" color={statusColors[review.status]} label={pretty(review.status)} sx={{ fontWeight: 600, fontSize: 11 }} />,
      created: <ValueCell>{new Date(review.createdAt).toLocaleString("en-IN")}</ValueCell>,
      action: (
        <ArgonButton
          size="small"
          variant={review.adminUnread ? "gradient" : "outlined"}
          color={review.adminUnread ? "error" : "info"}
          onClick={() => openReview(review)}
        >
          View
        </ArgonButton>
      ),
    })),
  }), [limit, page, reviews]);

  const saveReview = async () => {
    try {
      await updateServiceReview(selected._id, form);
      toast.success("Review updated successfully");
      setSelected(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.message || "Update failed");
    }
  };

  const saveRule = async (rule) => {
    try {
      await updateServiceReviewRule(rule._id, rule);
      toast.success("Rule saved successfully");
      load();
    } catch (error) {
      toast.error(error.response?.data?.message || "Rule save failed");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={2}>
          <ArgonTypography variant="h4">Automatic Service Review</ArgonTypography>
          <ArgonTypography variant="button" color="text">Tutor service issues, parent responses and resolution tracking</ArgonTypography>
        </ArgonBox>

        <Grid container spacing={2} mb={3}>
          {[["Active Reviews", counts.active, "#5e72e4"], ["Parent Response Pending", counts.pending, "#fb6340"], ["Resolved", counts.resolved, "#2dce89"]].map(([title, value, color]) => (
            <Grid item xs={12} md={4} key={title}>
              <Card sx={{ borderLeft: `4px solid ${color}`, boxShadow: "0 4px 18px rgba(0,0,0,.06)" }}>
                <ArgonBox p={2.25}>
                  <ArgonTypography variant="button" color="text" fontWeight="bold">{title}</ArgonTypography>
                  <ArgonTypography variant="h3" mt={0.5}>{value}</ArgonTypography>
                </ArgonBox>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card sx={{ overflow: "hidden" }}>
          <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="fullWidth">
            <Tab label="Reviews" />
            <Tab label="Rules" />
          </Tabs>

          {tab === 0 && (
            <>
              <ArgonBox p={2.5} display="flex" gap={2} flexWrap="wrap" alignItems="center" borderBottom="1px solid #eef0f4">
                <Dropdown
                  value={status}
                  width={230}
                  onChange={(event) => setStatus(event.target.value)}
                  options={[{ label: "All Status", value: "" }, ...statuses.map((value) => ({ label: pretty(value), value }))]}
                />
                <Dropdown
                  value={type}
                  width={230}
                  onChange={(event) => setType(event.target.value)}
                  options={[{ label: "All Issues", value: "" }, ...Object.entries(labels).map(([value, label]) => ({ label, value }))]}
                />
              </ArgonBox>

              {loading ? (
                <ArgonBox display="flex" justifyContent="center" p={6}><CircularProgress /></ArgonBox>
              ) : reviews.length === 0 ? (
                <ArgonBox p={3}><Alert severity="info">No service reviews found for the selected filters.</Alert></ArgonBox>
              ) : (
                <Table columns={tableData.columns} rows={tableData.rows} />
              )}

              <PaginationBar
                currentPage={page}
                rowsPerPage={limit}
                totalItems={pagination.total || 0}
                totalPages={pagination.totalPages || 0}
                onPageChange={(_, value) => setPage(value)}
                onRowsPerPageChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }}
              />
            </>
          )}

          {tab === 1 && (
            <ArgonBox p={{ xs: 2, md: 3 }}>
              <Grid container spacing={2.5}>
                {rules.map((rule, index) => (
                  <Grid item xs={12} key={rule._id}>
                    <Card
                      variant="outlined"
                      sx={{
                        boxShadow: "0 4px 14px rgba(52, 71, 103, 0.06)",
                        border: "1px solid #e9ecef",
                        borderRadius: 2,
                        overflow: "visible",
                      }}
                    >
                      <ArgonBox p={{ xs: 2, md: 2.5 }}>
                        <Grid container spacing={2.5} alignItems="center">
                          <Grid item xs={12} lg={3}>
                            <ArgonBox display="flex" alignItems="center" gap={1.5}>
                              <ArgonBox width="10px" height="44px" borderRadius="lg" bgColor={rule.enabled ? "success" : "secondary"} />
                              <ArgonBox>
                                <ArgonTypography variant="h6">{labels[rule.incidentType]}</ArgonTypography>
                                <ArgonTypography variant="caption" color="text">
                                  {rule.incidentType === "LATE_ARRIVAL" && "Tutor starts after scheduled time"}
                                  {rule.incidentType === "EARLY_CHECKOUT" && "Class ends before required duration"}
                                  {rule.incidentType === "IRREGULAR_CLASS_GAP" && "Unexpected gap between classes"}
                                </ArgonTypography>
                              </ArgonBox>
                            </ArgonBox>
                          </Grid>
                          <Grid item xs={12} lg={7}>
                            <Grid container spacing={2}>
                              {[
                                ["Admin threshold", "adminThreshold", "Admin alert occurrence"],
                                ["Parent threshold", "parentThreshold", "Parent popup occurrence"],
                                [rule.incidentType === "IRREGULAR_CLASS_GAP" ? "Allowed gap days" : "Tolerance minutes", rule.incidentType === "IRREGULAR_CLASS_GAP" ? "gapDays" : "toleranceMinutes", rule.incidentType === "IRREGULAR_CLASS_GAP" ? "Maximum permitted days" : "Grace period allowed"],
                              ].map(([label, key, helper]) => (
                                <Grid item xs={12} sm={4} key={key}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label={label}
                                    helperText={helper}
                                    value={rule[key]}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: key === "toleranceMinutes" ? 0 : 1 }}
                                    onChange={(event) => setRules((old) => old.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: Number(event.target.value) } : item))}
                                    sx={{ "& .MuiFormHelperText-root": { mx: 0, fontSize: 10.5 } }}
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          </Grid>
                          <Grid item xs={12} lg={2}>
                            <ArgonBox display="flex" flexDirection={{ xs: "row", lg: "column" }} alignItems="center" justifyContent="center" gap={1}>
                              <ArgonBox display="flex" alignItems="center">
                                <Switch checked={rule.enabled} onChange={(event) => setRules((old) => old.map((item, itemIndex) => itemIndex === index ? { ...item, enabled: event.target.checked } : item))} />
                                <ArgonTypography variant="caption" fontWeight="bold" color={rule.enabled ? "success" : "text"}>{rule.enabled ? "ACTIVE" : "INACTIVE"}</ArgonTypography>
                              </ArgonBox>
                              <ArgonButton fullWidth variant="contained" color="primary" size="small" onClick={() => saveRule(rule)}>Save Rule</ArgonButton>
                            </ArgonBox>
                          </Grid>
                        </Grid>
                      </ArgonBox>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </ArgonBox>
          )}
        </Card>
      </ArgonBox>
      <Footer />

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="md">
        <DialogTitle>Service Review Details</DialogTitle>
        <DialogContent>
          {selected && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>{labels[selected.incidentType]} occurred {selected.occurrenceCount} times in cycle {selected.cycleIndex + 1}.</Alert>
              <ArgonTypography variant="h6" mb={1}>Incident Evidence</ArgonTypography>
              {incidents.map((item) => (
                <ArgonBox key={item._id} mb={1} p={1.25} sx={{ background: "#f7f8fa", borderRadius: 1 }}>
                  <ArgonTypography variant="button">Occurrence {item.occurrenceNumber} · Variance {item.varianceMinutes} minutes · {new Date(item.detectedAt).toLocaleString("en-IN")}</ArgonTypography>
                </ArgonBox>
              ))}
              <ArgonBox mt={2}>
                <ArgonTypography variant="caption" fontWeight="bold">UPDATE STATUS</ArgonTypography>
                <Dropdown value={form.status} width="100%" onChange={(event) => setForm({ ...form, status: event.target.value })} options={["OPEN", "IN_PROGRESS", "RESOLVED", "DISMISSED"].map((value) => ({ label: pretty(value), value }))} />
              </ArgonBox>
              <TextField fullWidth multiline rows={3} label="Resolution note" value={form.resolutionNote} onChange={(event) => setForm({ ...form, resolutionNote: event.target.value })} sx={{ mt: 2 }} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <ArgonButton variant="contained" color="secondary" onClick={() => setSelected(null)}>
            Close
          </ArgonButton>
          <ArgonButton variant="contained" color="primary" onClick={saveReview}>
            Save Changes
          </ArgonButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

